/**
 * One pairing's collector detail, the hook-release registry the fleet
 * installs from, and the rollout-control write surface (GET + If-Match PUT).
 * The fleet itself is read by machine (./collectorMachines).
 */

import type {
  CollectorDetailResponse,
  CollectorRolloutResponse,
  CollectorRolloutUpdate,
  HookReleaseResponse,
} from '@/types/collectors'
import { apiClient } from './client'

export const collectors = {
  detail: async (
    project: string,
    cli: string,
    installationId: string
  ): Promise<CollectorDetailResponse> => {
    const response = await apiClient.get<CollectorDetailResponse>(
      `/api/v1/collectors/${encodeURIComponent(project)}/${encodeURIComponent(cli)}/${encodeURIComponent(installationId)}/`
    )
    return response.data
  },

  // Newest-first; the endpoint is rate-limited (60/min/IP), so callers keep
  // their fetch cadence low (see useLatestCollectorRelease's stale window).
  listReleases: async (params?: { limit?: number }): Promise<HookReleaseResponse[]> => {
    const response = await apiClient.get<HookReleaseResponse[]>(
      // Trailing slash: the endpoint is `@router.get("/")`, so the slash-less
      // form 307-redirects; calling the canonical path avoids the round-trip.
      '/api/v1/releases/',
      { params }
    )
    return response.data
  },

  // Returns 404 until the first release is published — callers set retry:false
  // so the pre-publish state surfaces as a clean empty rather than 3 retries.
  getRollout: async (): Promise<CollectorRolloutResponse> => {
    const response = await apiClient.get<CollectorRolloutResponse>(
      '/api/v1/setup/collector-rollout/'
    )
    return response.data
  },

  // ifMatch carries the current rollout_version for optimistic concurrency.
  // The server returns 412 when the version is stale, 422 for invalid fields.
  updateRollout: async (
    body: CollectorRolloutUpdate,
    ifMatch: number
  ): Promise<CollectorRolloutResponse> => {
    const response = await apiClient.put<CollectorRolloutResponse>(
      '/api/v1/setup/collector-rollout/',
      body,
      { headers: { 'If-Match': String(ifMatch) } }
    )
    return response.data
  },
}
